import { InstallButton } from "../components/InstallButton";
import { useState } from "react";
import { ApiError, get, patch, post } from "../api";
import { SUBJECTS } from "../../shared/constants";
import { Figure } from "../components/Figure";
import { runEnabled, setRunEnabled } from "../components/RunTransition";
import { effectMode, setEffectMode, type EffectMode } from "../components/fx";
import { ErrorBox, Field, Progress, Spinner } from "../components/ui";
import { Link, navigate } from "../router";
import { Floaties, Sparkles } from "../components/fx";
import { HeroScene } from "../components/HeroScene";
import { LuBadgeCheck, LuChartColumn, LuCircleCheck, LuCircleHelp, LuFlame, LuGamepad2, LuSettings, LuShirt, LuTrophy } from "react-icons/lu";
import { useApp, useLoad } from "../store";
import { canSpeak, readMode, setReadMode, speak, type ReadMode } from "../speech";

export function Profile() {
  const { me, setMe, toast } = useApp();
  const prog = useLoad(() => (me!.role === "student" ? get<any>("/api/progress") : Promise.resolve(null)), []);
  const [pw, setPw] = useState({ oldPassword: "", newPassword: "" });
  const [delPw, setDelPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [tab, setTab] = useState<"stats" | "badges" | "gear" | "settings">("stats");
  const [runOn, setRunOn] = useState(runEnabled());
  const [fxMode, setFxMode] = useState<EffectMode>(effectMode());
  const [rd, setRd] = useState<ReadMode>(readMode(me?.grade));
  if (!me) return null;
  const setting = async (body: any) => {
    const before = me;
    setMe({ ...me, ...body }); // optimistic: the switch moves at once, and is rolled back if the server refuses
    try { const r = await patch<{ user: any }>("/api/me/settings", body); setMe(r.user); toast("Gemt", "ok"); } catch (e) { setMe(before); toast(e instanceof ApiError ? e.message : "Noget gik galt.", "error"); }
  };
  const changePw = async (e: any) => {
    e.preventDefault();
    try { await post("/api/me/password", pw); setPw({ oldPassword: "", newPassword: "" }); toast("Adgangskoden er skiftet", "ok"); } catch (er) { toast(er instanceof ApiError ? er.message : "Noget gik galt.", "error"); }
  };
  const logout = async () => { await post("/api/auth/logout").catch(() => {}); setMe(null); navigate("/", true); };
  const remove = async (e: any) => {
    e.preventDefault();
    try { await post("/api/me/delete", { password: delPw, confirm }); setMe(null); navigate("/", true); toast("Din konto er slettet", "ok"); } catch (er) { toast(er instanceof ApiError ? er.message : "Noget gik galt.", "error"); }
  };
  const p = prog.data;
  return (
    <div className="stack rise">
      <section className="profile-banner pb2" data-spot aria-label="Din profil">
        <HeroScene palette="dusk" scene="castles" />
        <Sparkles n={16} />
        <Floaties kinds={["star", "coin", "gem"]} n={5} seed={7} />
        <div className="figure-stage profile-fig"><Figure equipped={me.avatar} framing="hero" label="Din figur – træk for at dreje den, klik for at få den til at reagere" /></div>
        <div className="who">
          <h1>{me.username}</h1>
          <p className="muted" style={{ margin: "0 0 6px" }}>{me.role === "student" ? `${me.title ?? "Nybegynder"} · ${me.grade}. klasse` : "Lærer"}</p>
          {me.role === "student" && <Progress value={me.levelProgress.into} max={me.levelProgress.needed} label="Fremgang til næste niveau" tone="lime" />}
        </div>
        {me.role === "student" && <div className="lvl-big" aria-label={`Niveau ${me.level}`} style={{ ["--p" as any]: `${Math.round((100 * me.levelProgress.into) / Math.max(1, me.levelProgress.needed))}%` }}><span>{me.level}</span></div>}
      </section>
      <div className="tabs filters" role="tablist" aria-label="Profil">
        {([["stats", "Statistik", LuChartColumn], ["badges", "Badges", LuBadgeCheck], ["gear", "Udstyr", LuShirt], ["settings", "Indstillinger", LuSettings]] as const).map(([id, name, Ic]) => (
          <button key={id} role="tab" aria-selected={tab === id} className={`fchip${tab === id ? " on" : ""}`} onClick={() => setTab(id)}><Ic aria-hidden="true" size={20} /> {name}</button>
        ))}
      </div>
      {tab === "badges" && <section className="card stack"><h2>Badges</h2><p className="muted">Se dem under Mål.</p><Link to="/praemier" className="btn">Se mine mål</Link></section>}
      {tab === "gear" && <section className="card stack"><h2>Udstyr</h2><p className="muted">Skift figur og tøj i Shop.</p><Link to="/butik" className="btn">Åbn Shop</Link></section>}
      {tab === "stats" && me.role === "student" && (prog.loading && !p ? <Spinner /> : prog.error ? <ErrorBox error={prog.error} retry={prog.reload} /> : p && (
        <section className="card stack">
          <h2>Din fremgang</h2>
          <ul className="statrow" aria-label="Dine tal">
            {([[LuCircleHelp, p.answered, "svar", "blue"], [LuCircleCheck, p.correct, "rigtige", "green"], [LuFlame, p.bestStreak, "stribe", "pink"], [LuGamepad2, p.rounds, "spil", "violet"], [LuTrophy, p.wins, "sejre", "gold"]] as const).map(([I, n, t, c]) => (
              <li key={t} className={`stat-tile ${c}`}><span className="st-ico"><I size={24} aria-hidden="true" /></span><b>{n}</b><small>{t}</small></li>
            ))}
          </ul>
          <div className="stack">
            {p.subjects.map((s: any) => (
              <div key={s.subject}>
                <div className="row between"><span>{SUBJECTS.find((x) => x.id === s.subject)?.name}</span><small className="muted">{s.accuracy === null ? "Ikke spillet endnu" : `${s.accuracy}% rigtige (${s.correct}/${s.answered})`}</small></div>
                <Progress value={s.accuracy ?? 0} max={100} label={`${s.accuracy ?? 0} procent rigtige`} />
              </div>
            ))}
          </div>
        </section>
      ))}
      {tab === "settings" && <>
      <section className="card stack">
        <h2>Animationer</h2>
        <label className="check"><input type="checkbox" checked={runOn} onChange={(e: any) => { setRunOn(e.target.checked); setRunEnabled(e.target.checked); }} /> <span>Min figur løber hen over skærmen, når jeg skifter side</span></label>
        <div className="field fx-choice" role="group" aria-labelledby="fx-label">
          <span id="fx-label"><b>Pynt-effekter</b></span>
          <div className="segment">
            {([["auto", "Automatisk"], ["on", "Altid"], ["off", "Slået fra"]] as const).map(([id, name]) => (
              <button type="button" key={id} aria-pressed={fxMode === id} onClick={() => { setFxMode(id); setEffectMode(id); }}>{name}</button>
            ))}
          </div>
          
        </div>
      </section>
      <section className="card stack">
        <h2>Oplæsning</h2>
        {canSpeak() ? (
          <div className="field fx-choice" role="group" aria-labelledby="rd-label">
            <span id="rd-label"><b>Læs spørgsmålene op</b></span>
            <div className="segment">
              {([["auto", "Automatisk"], ["tap", "Når jeg trykker"], ["off", "Ingen oplæsning"]] as const).map(([id, name]) => (
                <button type="button" key={id} aria-pressed={rd === id} onClick={() => { setRd(id); setReadMode(id); }}>{name}</button>
              ))}
            </div>
            <div className="row"><button type="button" className="btn ghost small" onClick={() => speak("Hej! Sådan lyder stemmen, der læser spørgsmålene op.")}>Prøv stemmen</button></div>
            
          </div>
        ) : <p className="muted">Din browser kan ikke læse op. Prøv Chrome, Edge eller Safari.</p>}
      </section>
      <section className="card stack">
        <h2>Hent som app</h2>
        <p className="muted">Læg EduBattle på din startskærm.</p>
        <div className="row"><InstallButton variant="card" /></div>
      </section>
      <section className="card stack">
        <h2>Privatliv</h2>
        <p className="muted">Andre ser kun brugernavn, niveau, titel og figur.</p>
        {me.role === "student" && (
          <>
            <label className="check"><input type="checkbox" checked={me.showOnLeaderboards} onChange={(e: any) => setting({ showOnLeaderboards: e.target.checked })} /> Vis mig på ranglisterne</label>
            <label className="check"><input type="checkbox" checked={me.shareNameWithTeacher} onChange={(e: any) => setting({ shareNameWithTeacher: e.target.checked })} /> Min lærer må se mit rigtige navn i klasserapporten</label>
          </>
        )}
        <div className="row">
          <a className="btn ghost" href="/api/me/export" download>Hent mine data (JSON)</a>
          <button className="btn ghost" onClick={logout}>Log ud</button>
        </div>
      </section>
      <section className="card stack">
        <h2>Skift adgangskode</h2>
        <form className="stack" onSubmit={changePw}>
          <Field label="Nuværende adgangskode"><input type="password" autoComplete="current-password" value={pw.oldPassword} onChange={(e: any) => setPw({ ...pw, oldPassword: e.target.value })} /></Field>
          <Field label="Ny adgangskode" hint="Mindst 8 tegn"><input type="password" autoComplete="new-password" value={pw.newPassword} onChange={(e: any) => setPw({ ...pw, newPassword: e.target.value })} /></Field>
          <button className="btn" disabled={!pw.oldPassword || !pw.newPassword}>Skift</button>
        </form>
      </section>
      <section className="card stack">
        <h2>Slet min konto</h2>
        {!showDelete ? <button className="btn danger" onClick={() => setShowDelete(true)}>Slet min konto…</button> : (
          <form className="stack" onSubmit={remove}>
            <p className="notice error">Det sletter din konto, dine mønter og alt, du har optjent. Det kan ikke fortrydes.</p>
            <Field label="Adgangskode"><input type="password" value={delPw} onChange={(e: any) => setDelPw(e.target.value)} autoComplete="current-password" /></Field>
            <Field label="Skriv SLET for at bekræfte"><input value={confirm} onChange={(e: any) => setConfirm(e.target.value)} /></Field>
            <div className="row"><button className="btn danger" disabled={!delPw || confirm !== "SLET"}>Slet for altid</button><button type="button" className="btn ghost" onClick={() => setShowDelete(false)}>Fortryd</button></div>
          </form>
        )}
      </section>
      </>}
    </div>
  );
}

